import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Sparkles, AlertTriangle, ShieldAlert, ShieldCheck, Scale, Layers, Target,
  Coins, TrendingUp, TrendingDown, Minus, Gauge, Users, History, RefreshCw,
  Settings2, BellRing, Bell, CircleCheck, CircleAlert, Info, Code2, Lightbulb,
  ArrowRight, Clock,
} from 'lucide-react';
import { useGet } from '@/lib/useData';
import api from '@/lib/api';
import { fmtEur, fmtInt, fmtNum, fmtDateTime } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

// --------------------------------------------------------------------------- //
//  Helpers                                                                    //
// --------------------------------------------------------------------------- //
const STATUT = {
  rentable: { dot: 'bg-emerald-500', chip: 'border-emerald-300 text-emerald-700 dark:text-emerald-300', label: 'Rentable' },
  surveille: { dot: 'bg-amber-500', chip: 'border-amber-300 text-amber-700 dark:text-amber-300', label: 'À surveiller' },
  non_rentable: { dot: 'bg-rose-500', chip: 'border-rose-300 text-rose-700 dark:text-rose-300', label: 'Non rentable' },
  na: { dot: 'bg-muted-foreground/40', chip: 'border-muted text-muted-foreground', label: 'n/d' },
};
const CONF = {
  elevee: { chip: 'border-emerald-300 text-emerald-700 dark:text-emerald-300', label: 'confiance élevée' },
  moyenne: { chip: 'border-amber-300 text-amber-700 dark:text-amber-300', label: 'confiance moyenne' },
  faible: { chip: 'border-rose-300 text-rose-700 dark:text-rose-300', label: 'confiance faible' },
};

const eur0 = (v) => fmtEur(v, 0);
const signColor = (v) => (v > 0 ? 'text-emerald-600 dark:text-emerald-400' : v < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-foreground');

function InlineBold({ text }) {
  const parts = String(text || '').split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => (i % 2 === 1 ? <strong key={i} className="font-semibold text-foreground">{p}</strong> : <span key={i}>{p}</span>));
}

function Narrative({ text }) {
  const lines = String(text || '').split('\n').map((l) => l.trim()).filter(Boolean);
  return (
    <div className="space-y-2 text-sm leading-7 text-muted-foreground" data-testid="decision-narrative">
      {lines.map((l, i) => {
        const head = l.match(/^\*\*(.+?)\*\*\s*:?\s*(.*)$/);
        if (head) {
          return (
            <div key={i} className="pt-1">
              <div className="font-display text-sm font-semibold accent-text">{head[1]}</div>
              {head[2] && <p className="mt-0.5"><InlineBold text={head[2]} /></p>}
            </div>
          );
        }
        if (l.startsWith('- ')) {
          return (
            <div key={i} className="flex gap-2 pl-1">
              <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 accent-text" />
              <span><InlineBold text={l.slice(2)} /></span>
            </div>
          );
        }
        return <p key={i}><InlineBold text={l} /></p>;
      })}
    </div>
  );
}

const Panel = ({ children, className = '', ...rest }) => (
  <div className={`rounded-2xl border bg-card p-4 shadow-sm ${className}`} {...rest}>{children}</div>
);

const SectionTitle = ({ icon: Icon, title, sub, right }) => (
  <div className="mb-3 flex items-start justify-between gap-2">
    <div className="flex items-center gap-2">
      {Icon && <span className="grid h-8 w-8 place-items-center rounded-xl" style={{ background: 'var(--tenant-accent-050)', color: 'var(--tenant-accent-600)' }}><Icon className="h-4 w-4" /></span>}
      <div>
        <div className="font-display text-sm font-semibold">{title}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </div>
    {right}
  </div>
);

function Trend3m({ serie, tendance }) {
  const vals = (serie || []).map((s) => s.marge_proj_eur);
  const max = Math.max(1, ...vals.map((v) => Math.abs(v)));
  const Icon = tendance === 'hausse' ? TrendingUp : tendance === 'baisse' ? TrendingDown : Minus;
  const color = tendance === 'hausse' ? 'text-emerald-500' : tendance === 'baisse' ? 'text-rose-500' : 'text-muted-foreground';
  return (
    <div className="flex items-center gap-2" data-testid="collab-trend">
      <div className="flex items-end gap-1" style={{ height: 22 }}>
        {(serie || []).map((s, i) => {
          const h = Math.max(3, Math.round((Math.abs(s.marge_proj_eur) / max) * 22));
          const neg = s.marge_proj_eur < 0;
          return <span key={i} title={`${s.ym}: ${eur0(s.marge_proj_eur)}`} className={`w-1.5 rounded-sm ${neg ? 'bg-rose-400' : 'bg-emerald-400'}`} style={{ height: h }} />;
        })}
      </div>
      <Icon className={`h-3.5 w-3.5 ${color}`} />
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Config des seuils d'alerte (versionné par date d'effet)                    //
// --------------------------------------------------------------------------- //
function AlertConfigDialog({ ym, open, onOpenChange, onSaved }) {
  const pq = useGet('/social/params', { ym }, open);
  const current = pq.data?.current;
  const [form, setForm] = useState(null);
  const [eff, setEff] = useState(`${ym}-01`);
  const [saving, setSaving] = useState(false);

  const model = useMemo(() => {
    if (form) return form;
    if (!current) return null;
    return {
      decision_alerte_marge_min_eur: current.decision_alerte_marge_min_eur ?? 0,
      decision_alerte_couverture_min: current.decision_alerte_couverture_min ?? 1,
      decision_alerte_productivite_min: current.decision_alerte_productivite_min ?? 0,
      decision_alerte_cout_acq_max: current.decision_alerte_cout_acq_max ?? 0,
      decision_fiabilite_min_taux: current.decision_fiabilite_min_taux ?? 0.5,
    };
  }, [form, current]);

  const set = (k, v) => setForm({ ...(model || {}), [k]: v });

  const save = async () => {
    if (!current || !model) return;
    setSaving(true);
    try {
      const merged = { ...current, ...model };
      // normalise en nombres
      ['decision_alerte_marge_min_eur', 'decision_alerte_couverture_min',
       'decision_alerte_productivite_min', 'decision_alerte_cout_acq_max',
       'decision_fiabilite_min_taux'].forEach((k) => { merged[k] = parseFloat(merged[k]) || 0; });
      await api.post('/social/params', {
        params: merged, effective_date: eff,
        note: "Seuils d'alerte — Aide à la décision",
      });
      toast.success('Seuils enregistrés', { description: `Version effective au ${eff}` });
      onOpenChange(false);
      setForm(null);
      onSaved?.();
    } catch (e) {
      toast.error("Échec de l'enregistrement", { description: e?.response?.data?.detail || 'Erreur' });
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ k, label, unit, step = '1' }) => (
    <div className="space-y-1">
      <Label htmlFor={`alert-${k}`} className="text-xs">{label} {unit && <span className="text-muted-foreground">({unit})</span>}</Label>
      <Input id={`alert-${k}`} type="number" step={step} value={model?.[k] ?? ''} data-testid={`alert-cfg-${k}`}
        onChange={(e) => set(k, e.target.value)} className="h-9 rounded-xl" />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="alert-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing className="h-4 w-4 accent-text" /> Seuils d'alerte — Aide à la décision</DialogTitle>
          <DialogDescription>Paramètres versionnés par date d'effet : un changement n'affecte jamais l'interprétation des mois antérieurs. Mettre 0 désactive l'alerte (productivité / coût d'acquisition).</DialogDescription>
        </DialogHeader>
        {!current ? <Skeleton className="h-40 rounded-xl" /> : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field k="decision_alerte_marge_min_eur" label="Marge projetée minimale" unit="€" step="100" />
            <Field k="decision_alerte_couverture_min" label="Couverture minimale" unit="×" step="0.01" />
            <Field k="decision_alerte_productivite_min" label="Productivité minimale" unit="BS/jour" step="0.1" />
            <Field k="decision_alerte_cout_acq_max" label="Coût d'acquisition max" unit="€/BS" step="1" />
            <div className="space-y-1">
              <Label htmlFor="alert-fiab" className="text-xs">Seuil « mois fiable » <span className="text-muted-foreground">(taux, 0-1)</span></Label>
              <Input id="alert-fiab" type="number" step="0.05" min="0" max="1" value={model?.decision_fiabilite_min_taux ?? ''} data-testid="alert-cfg-fiab"
                onChange={(e) => set('decision_fiabilite_min_taux', e.target.value)} className="h-9 rounded-xl" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="alert-eff" className="text-xs">Date d'effet</Label>
              <Input id="alert-eff" type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="h-9 rounded-xl" data-testid="alert-cfg-effective-date" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="alert-cfg-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving || !current} style={{ background: 'var(--tenant-accent-500)' }} data-testid="alert-cfg-save">
            {saving ? <RefreshCw className="mr-1 h-4 w-4 animate-spin" /> : <Settings2 className="mr-1 h-4 w-4" />} Enregistrer la version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------- //
//  Panneau principal                                                          //
// --------------------------------------------------------------------------- //
export function DecisionPanel({ ym, allouer, population = 'all', statut = 'all' }) {
  const qc = useQueryClient();
  const q = useGet('/social/decision', { ym, allouer_structure: allouer, population, statut }, !!ym);
  const d = q.data;

  const [gen, setGen] = useState(null);        // {narrative, llm_available, error, created_at, model, id}
  const [generating, setGenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [showAllCollab, setShowAllCollab] = useState(false);
  const [showFactual, setShowFactual] = useState(false);

  const hq = useGet('/social/decision/history', { ym }, showHistory && !!ym);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/social/decision/generate', { ym, allouer_structure: allouer, population, statut });
      setGen(data);
      if (data.llm_available) toast.success('Synthèse générée et historisée');
      else toast.warning('IA indisponible', { description: 'Le payload factuel reste exploitable ci-dessous.' });
      qc.invalidateQueries({ queryKey: ['/social/decision/history'] });
    } catch (e) {
      toast.error('Échec de la génération', { description: e?.response?.data?.detail || 'Erreur serveur' });
    } finally {
      setGenerating(false);
    }
  };

  if (q.isLoading || !d) {
    return <Panel data-testid="decision-panel-loading"><Skeleton className="h-6 w-52" /><Skeleton className="mt-3 h-24 rounded-xl" /></Panel>;
  }

  const fiab = d.fiabilite || {};

  // --- Bandeau fiabilité (impossible à manquer si prématuré) ---------------- //
  const FiabBanner = () => {
    if (!d.has_data) {
      return (
        <Panel className="border-muted" data-testid="decision-fiab-banner">
          <div className="flex items-center gap-3"><Info className="h-5 w-5 text-muted-foreground" /><div className="text-sm">{fiab.message}</div></div>
        </Panel>
      );
    }
    if (fiab.premature) {
      return (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 dark:border-amber-500/60 dark:bg-amber-500/10" data-testid="decision-fiab-banner">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-7 w-7 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-display text-base font-bold text-amber-800 dark:text-amber-200">Fiabilité faible — décision prématurée</span>
                <Badge className="border-transparent bg-amber-500 text-white" data-testid="decision-fiab-pct">{fiab.jours_ecoules}/{fiab.jours_attendus} j · {fiab.taux_avancement_pct} %</Badge>
              </div>
              <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">{fiab.message}</p>
              <div className="mt-2 h-2 w-full max-w-md overflow-hidden rounded-full bg-amber-200 dark:bg-amber-900/40">
                <div className="h-full rounded-full bg-amber-500" style={{ width: `${Math.min(100, fiab.taux_avancement_pct)}%` }} />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <Panel data-testid="decision-fiab-banner">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <ShieldCheck className="h-5 w-5 text-emerald-500" />
          <span className="text-sm font-medium">Fiabilité {fiab.niveau} — {fiab.jours_ecoules}/{fiab.jours_attendus} j ({fiab.taux_avancement_pct} %)</span>
          <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Source BS : {fiab.source_label}</Badge>
        </div>
      </Panel>
    );
  };

  // --- Périmètre & filtres -------------------------------------------------- //
  const Perimetre = () => (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="decision-perimetre">
      <Badge variant="outline" className="gap-1"><Info className="h-3 w-3" /> {fiab.perimetre}</Badge>
      <Badge variant="secondary">Lecture active : {fiab.filtres?.lecture_active === 'allouee' ? 'structure allouée' : 'structure non allouée'}</Badge>
      {population !== 'all' && <Badge variant="secondary">Population : {population}</Badge>}
      {statut !== 'all' && <Badge variant="secondary">Statut : {statut}</Badge>}
    </div>
  );

  if (!d.has_data) {
    return (
      <div className="space-y-3" data-testid="decision-panel">
        <SectionTitle icon={Sparkles} title="Aide à la décision" sub={d.label} />
        <FiabBanner />
        <Perimetre />
      </div>
    );
  }

  const v = d.verdict || {};
  const active = v.lecture_active;
  const ReadingCard = ({ mode, data: rd, title, subtitle }) => {
    const isActive = active === mode;
    return (
      <div className={`rounded-2xl border p-4 ${isActive ? 'ring-2' : ''}`}
        style={isActive ? { borderColor: 'var(--tenant-accent-300)', boxShadow: '0 0 0 2px var(--tenant-accent-200)' } : {}}
        data-testid={`decision-reading-${mode}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs font-medium">
            <Layers className="h-3.5 w-3.5 accent-text" /> {title}
          </div>
          {isActive && <Badge className="border-transparent" style={{ background: 'var(--tenant-accent-500)' }}>active</Badge>}
        </div>
        <div className="text-[11px] text-muted-foreground">{subtitle}</div>
        <div className={`mt-2 kpi-value text-2xl tabular ${signColor(rd.marge_totale_proj_eur)}`} data-testid={`decision-reading-${mode}-marge`}>{fmtEur(rd.marge_totale_proj_eur, 0)}</div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
          <Badge variant="outline" className="gap-1"><Target className="h-3 w-3" /> Seuil {rd.seuil_couverture == null ? 'n/c' : `×${fmtNum(rd.seuil_couverture, 2)}`}</Badge>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" />{rd.nb_rentable}</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" />{rd.nb_surveille}</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-500" />{rd.nb_non_rentable}</span>
        </div>
      </div>
    );
  };

  const collabAll = d.collaborateurs || [];
  const collabShown = showAllCollab ? collabAll : collabAll.slice(0, 6);
  const alertesActives = (d.alertes || []).filter((a) => a.active);
  const nbDecl = d.compteurs?.alertes_declenchees || 0;

  return (
    <div className="space-y-4" data-testid="decision-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionTitle icon={Sparkles} title="Aide à la décision" sub={`Verdict mensuel — ${d.label}`} />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHistory((s) => !s)} data-testid="decision-history-toggle">
            <History className="mr-1 h-4 w-4" /> Historique
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowFactual((s) => !s)} data-testid="decision-factual-toggle">
            <Code2 className="mr-1 h-4 w-4" /> Nombres factuels
          </Button>
          <Button size="sm" onClick={generate} disabled={generating} style={{ background: 'var(--tenant-accent-500)' }} data-testid="decision-generate-btn">
            {generating ? <RefreshCw className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
            {generating ? 'Génération…' : 'Générer la synthèse IA'}
          </Button>
        </div>
      </div>

      <FiabBanner />
      <Perimetre />

      {/* Verdict global */}
      <Panel data-testid="decision-verdict">
        <SectionTitle icon={Scale} title="Verdict du mois"
          sub="Le statut est décidé sur la projection de fin de mois"
          right={<Badge variant="outline" className={`gap-1 ${v.au_dessus_point_mort ? 'border-emerald-300 text-emerald-700 dark:text-emerald-300' : 'border-rose-300 text-rose-700 dark:text-rose-300'}`} data-testid="decision-breakeven-badge">
            {v.au_dessus_point_mort ? <CircleCheck className="h-3.5 w-3.5" /> : <CircleAlert className="h-3.5 w-3.5" />} {v.au_dessus_point_mort ? 'Au-dessus du point mort' : 'Sous le point mort'}
          </Badge>} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-xl border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Écart au point mort (résultat projeté)</div>
            <div className={`kpi-value mt-1 text-2xl tabular ${signColor(v.ecart_point_mort_global_eur)}`} data-testid="decision-resultat-proj">{fmtEur(v.ecart_point_mort_global_eur, 0)}</div>
          </div>
          <div className="rounded-xl border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">CA net projeté</div>
            <div className="kpi-value mt-1 text-2xl tabular">{fmtEur(v.ca_projete_eur, 0)}</div>
          </div>
          <div className="rounded-xl border bg-muted/20 p-3">
            <div className="text-xs text-muted-foreground">Coût projeté (toutes populations + structure)</div>
            <div className="kpi-value mt-1 text-2xl tabular">{fmtEur(v.cout_projete_eur, 0)}</div>
          </div>
        </div>

        {/* Deux lectures côte à côte + explication de l'écart */}
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <ReadingCard mode="non_allouee" data={v.lecture_non_allouee} title="Structure NON allouée" subtitle="marge directe (hors frais fixes)" />
          <ReadingCard mode="allouee" data={v.lecture_allouee} title="Structure allouée" subtitle="marge nette (frais fixes répartis)" />
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-xl border-l-4 p-3 text-sm" style={{ borderColor: 'var(--tenant-accent-400)', background: 'var(--tenant-accent-050)' }} data-testid="decision-ecart-explication">
          <Info className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
          <span>{v.explication_ecart}</span>
        </div>
      </Panel>

      {/* Ce qui pèse */}
      <Panel data-testid="decision-causes">
        <SectionTitle icon={Coins} title="Ce qui pèse sur le résultat" sub="postes classés par montant (projeté)" />
        <div className="space-y-2">
          {(d.ce_qui_pese || []).map((c, i) => (
            <div key={i} className="flex items-center justify-between gap-3 rounded-xl border bg-background/60 p-3" data-testid={`decision-cause-${i}`}>
              <div className="flex items-center gap-3">
                <span className="grid h-7 w-7 place-items-center rounded-lg text-xs font-bold" style={{ background: 'var(--tenant-accent-050)', color: 'var(--tenant-accent-600)' }}>{i + 1}</span>
                <div>
                  <div className="text-sm font-medium">{c.poste}</div>
                  <div className="text-xs text-muted-foreground">{c.detail}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="tabular font-semibold">{fmtEur(c.montant_eur, 0)}</div>
                <Badge variant="outline" className="text-[10px]">{c.sens === 'charge' ? 'charge' : 'manque à gagner'}</Badge>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Décisions recommandées */}
      <Panel data-testid="decision-recos">
        <SectionTitle icon={Lightbulb} title="Décisions recommandées"
          sub="calculées par règles déterministes (backend) — l'IA ne fait que les rédiger" />
        <div className="space-y-2">
          {(d.decisions || []).map((rec, i) => {
            const c = CONF[rec.confiance] || CONF.moyenne;
            return (
              <div key={i} className="flex items-start justify-between gap-3 rounded-xl border bg-background/60 p-3" data-testid={`decision-reco-${i}`}>
                <div className="flex items-start gap-3">
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
                  <div>
                    <div className="text-sm font-medium">{rec.action}</div>
                    <div className="text-xs text-muted-foreground">{rec.base}</div>
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  {rec.gain_eur != null && <div className="tabular font-semibold text-emerald-600 dark:text-emerald-400">+{fmtEur(rec.gain_eur, 0)}</div>}
                  <span className={`mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${c.chip}`}>{c.label}</span>
                </div>
              </div>
            );
          })}
          {(!d.decisions || d.decisions.length === 0) && <p className="text-xs text-muted-foreground">Aucune décision saillante sur ce mois.</p>}
        </div>
      </Panel>

      {/* Alertes */}
      <Panel data-testid="decision-alertes">
        <SectionTitle icon={nbDecl > 0 ? BellRing : Bell} title="Alertes configurables"
          sub={nbDecl > 0 ? `${nbDecl} alerte(s) déclenchée(s)` : 'aucune alerte déclenchée'}
          right={<Button variant="outline" size="sm" onClick={() => setCfgOpen(true)} data-testid="decision-alertes-config-btn"><Settings2 className="mr-1 h-4 w-4" /> Configurer les seuils</Button>} />
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(d.alertes || []).map((a) => (
            <div key={a.cle}
              className={`flex items-center justify-between gap-2 rounded-xl border p-3 ${a.declenchee ? 'border-rose-300 bg-rose-50 dark:border-rose-500/50 dark:bg-rose-500/10' : a.active ? 'bg-background/60' : 'bg-muted/20 opacity-70'}`}
              data-testid={`decision-alerte-${a.cle}`}>
              <div className="flex items-center gap-2">
                {a.declenchee ? <CircleAlert className="h-4 w-4 text-rose-500" /> : a.active ? <CircleCheck className="h-4 w-4 text-emerald-500" /> : <Bell className="h-4 w-4 text-muted-foreground" />}
                <div>
                  <div className="text-sm font-medium">{a.libelle}</div>
                  <div className="text-xs text-muted-foreground">
                    {a.active ? <>seuil {a.sens === 'min' ? '≥' : '≤'} {fmtNum(a.seuil, a.unite === '×' ? 2 : 0)} {a.unite}</> : 'désactivée (seuil = 0)'}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`tabular font-semibold ${a.declenchee ? 'text-rose-600 dark:text-rose-400' : ''}`}>{fmtNum(a.valeur, a.unite === '×' ? 2 : (a.unite === 'BS/j' ? 2 : 0))} {a.unite}</div>
                {a.declenchee && <Badge className="border-transparent bg-rose-500 text-white text-[10px]">déclenchée</Badge>}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Diagnostic par collaborateur */}
      <Panel data-testid="decision-collaborateurs">
        <SectionTitle icon={Users} title="Diagnostic par collaborateur"
          sub={`${collabAll.length} collaborateur(s) · coût, CA, marge projetée, tendance 3 mois, recommandation`} />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {collabShown.map((c) => {
            const st = STATUT[c.statut] || STATUT.na;
            const deficit = c.marge_proj_eur < 0;
            return (
              <div key={c.matricule}
                className={`rounded-2xl border p-3 ${deficit ? 'border-rose-200 dark:border-rose-500/40' : c.statut === 'rentable' ? 'border-emerald-200 dark:border-emerald-500/40' : ''}`}
                data-testid={`decision-collab-${c.matricule}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{c.label}</div>
                    <Badge variant="secondary" className="mt-0.5 text-[10px]">{c.population_label}</Badge>
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${st.chip}`}><span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}</span>
                </div>
                <div className="mt-2 flex items-end justify-between">
                  <div>
                    <div className="text-[11px] text-muted-foreground">Marge projetée</div>
                    <div className={`kpi-value text-xl tabular ${signColor(c.marge_proj_eur)}`} data-testid={`decision-collab-${c.matricule}-marge`}>{fmtEur(c.marge_proj_eur, 0)}</div>
                  </div>
                  <Trend3m serie={c.tendance_3m} tendance={c.tendance} />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-1 text-[11px] text-muted-foreground">
                  <div><div className="text-muted-foreground/70">CA</div><div className="tabular text-foreground">{eur0(c.ca_proj_eur)}</div></div>
                  <div><div className="text-muted-foreground/70">Coût</div><div className="tabular text-foreground">{eur0(c.cout_proj_eur)}</div></div>
                  <div><div className="text-muted-foreground/70">Couv.</div><div className="tabular text-foreground">{c.ratio_proj == null ? 'n/c' : `×${fmtNum(c.ratio_proj, 2)}`}</div></div>
                </div>
                <div className="mt-2 rounded-lg bg-muted/30 px-2 py-1.5 text-[11px]">{c.recommandation}</div>
              </div>
            );
          })}
        </div>
        {collabAll.length > 6 && (
          <div className="mt-3 text-center">
            <Button variant="ghost" size="sm" onClick={() => setShowAllCollab((s) => !s)} data-testid="decision-collab-toggle">
              {showAllCollab ? 'Réduire' : `Voir les ${collabAll.length} collaborateurs`}
            </Button>
          </div>
        )}
      </Panel>

      {/* Synthèse IA générée */}
      {gen && (
        <Panel data-testid="decision-synthese">
          <SectionTitle icon={Sparkles} title="Synthèse rédigée par l'IA"
            sub={gen.created_at ? `Générée le ${fmtDateTime(gen.created_at)} · ${gen.model}` : gen.model}
            right={gen.llm_available
              ? <Badge variant="outline" className="gap-1 border-emerald-300 text-emerald-700 dark:text-emerald-300"><ShieldCheck className="h-3.5 w-3.5" /> historisée</Badge>
              : <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700 dark:text-amber-300"><AlertTriangle className="h-3.5 w-3.5" /> IA indisponible</Badge>} />
          {gen.llm_available
            ? <Narrative text={gen.narrative} />
            : (
              <div className="space-y-3" data-testid="decision-llm-fallback">
                <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4" /> {gen.error || "Synthèse IA indisponible."} Le payload factuel reste exploitable ci-dessous.
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => setShowRaw((s) => !s)} data-testid="decision-raw-toggle"><Code2 className="mr-1 h-4 w-4" /> {showRaw ? 'Masquer' : 'Afficher'} le payload brut</Button>
                </div>
                {showRaw && <RawPayloadTable payload={gen.payload} />}
              </div>
            )}
        </Panel>
      )}

      {/* Historique immuable */}
      {showHistory && (
        <Panel data-testid="decision-history">
          <SectionTitle icon={History} title="Historique des synthèses" sub="immuable — jamais réécrit" />
          {hq.isLoading ? <Skeleton className="h-16 rounded-xl" /> : (
            (hq.data?.items || []).length === 0
              ? <p className="text-xs text-muted-foreground">Aucune synthèse historisée pour ce mois.</p>
              : (
                <div className="space-y-2">
                  {(hq.data.items || []).map((it) => (
                    <div key={it.id} className="rounded-xl border bg-background/60 p-3" data-testid={`decision-history-item-${it.id}`}>
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="font-medium">{fmtDateTime(it.created_at)}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-[10px]">{it.created_by}</Badge>
                          {it.llm_available ? <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 dark:text-emerald-300">IA</Badge> : <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 dark:text-amber-300">repli</Badge>}
                        </div>
                      </div>
                      {it.narrative && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{it.narrative.replace(/\*\*/g, '').slice(0, 220)}…</p>}
                    </div>
                  ))}
                </div>
              )
          )}
        </Panel>
      )}

      <AlertConfigDialog ym={ym} open={cfgOpen} onOpenChange={setCfgOpen}
        onSaved={() => qc.invalidateQueries({ queryKey: ['/social/decision'] })} />

      {/* Transparence : les nombres factuels qui fondent la synthèse (repli hors-ligne) */}
      {showFactual && (
        <Panel data-testid="decision-factual">
          <SectionTitle icon={Code2} title="Nombres factuels (payload)"
            sub="source unique des chiffres — l'IA ne fait que les rédiger, elle n'en invente aucun" />
          <RawPayloadTable payload={d} />
        </Panel>
      )}
    </div>
  );
}

// Tableau du payload brut (repli quand l'IA est indisponible) ---------------- //
function RawPayloadTable({ payload }) {
  const rows = [];
  const push = (label, val) => rows.push({ label, val });
  const v = payload?.verdict || {};
  push('Résultat projeté (écart point mort)', fmtEur(v.ecart_point_mort_global_eur, 0));
  push('CA net projeté', fmtEur(v.ca_projete_eur, 0));
  push('Coût projeté', fmtEur(v.cout_projete_eur, 0));
  push('Marge — structure non allouée', fmtEur(v.lecture_non_allouee?.marge_totale_proj_eur, 0));
  push('Marge — structure allouée', fmtEur(v.lecture_allouee?.marge_totale_proj_eur, 0));
  push('Écart entre lectures (structure)', fmtEur(v.ecart_lectures_eur, 0));
  (payload?.ce_qui_pese || []).forEach((c) => push(`Poste : ${c.poste}`, fmtEur(c.montant_eur, 0)));
  (payload?.alertes || []).filter((a) => a.active).forEach((a) => push(`Alerte : ${a.libelle}`, `${fmtNum(a.valeur, 2)} ${a.unite} (seuil ${fmtNum(a.seuil, 2)})`));
  return (
    <div className="overflow-hidden rounded-xl border" data-testid="decision-raw-table">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="px-3 py-2 text-muted-foreground">{r.label}</td>
              <td className="px-3 py-2 text-right tabular font-medium">{r.val}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
